/**
 * Notification policy — the admin-governed Notification Control Panel.
 *
 * One row per (eventType × role × channel). `role` is a staff role or the
 * `ORG_POLICY_ROLE` sentinel ('ALL') for the org-wide default; recipients
 * without a staff role (external client contacts) always resolve to 'ALL'.
 * Resolution order: role-specific row → org-wide row → DEFAULT_NOTIFICATION_POLICY
 * below, which encodes CURRENT behaviour exactly so nothing changes for users
 * until an admin edits policy.
 *
 * `BROWSER_PUSH` is a new channel: it defaults ON but only ever reaches
 * devices where the user explicitly subscribed (per-device toggle), so the
 * default alone changes nothing.
 */

import { STAFF_ROLES } from './roles';

export const NOTIFICATION_EVENT_TYPES = [
  'assignment',
  'chat_mention',
  'leave_request',
  'leave_decision',
  'proposal',
  'invoice',
  'meeting',
  'compliance_deadline',
  'document_expiry',
  'morning_digest',
] as const;
export type NotificationEventType = (typeof NOTIFICATION_EVENT_TYPES)[number];

export const NOTIFICATION_EVENT_LABELS: Record<NotificationEventType, string> = {
  assignment: 'Compliance / engagement assignment',
  chat_mention: 'Chat mention / DM',
  leave_request: 'Leave request submitted',
  leave_decision: 'Leave decision',
  proposal: 'Proposal sent / signed / negotiation / declined',
  invoice: 'Invoice issued / reminder / overdue / paid',
  meeting: 'Meeting invite / update / cancel / reminder',
  compliance_deadline: 'Compliance due soon / overdue',
  document_expiry: 'Legal document expiring / expired',
  morning_digest: 'Morning digest',
};

export const NOTIFICATION_CHANNELS = ['EMAIL', 'IN_APP', 'MOBILE_PUSH', 'BROWSER_PUSH'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  EMAIL: 'Email',
  IN_APP: 'In-app',
  MOBILE_PUSH: 'Mobile push',
  BROWSER_PUSH: 'Browser push',
};

/** Policy-row role value for the org-wide default (a real role otherwise). */
export const ORG_POLICY_ROLE = 'ALL';

/** Roles an admin can edit policy for: org-wide default + each staff role. */
export const POLICY_ROLES = [ORG_POLICY_ROLE, ...STAFF_ROLES] as const;
export type PolicyRole = (typeof POLICY_ROLES)[number];

export function isNotificationEventType(value: string): value is NotificationEventType {
  return (NOTIFICATION_EVENT_TYPES as readonly string[]).includes(value);
}

export function isNotificationChannel(value: string): value is NotificationChannel {
  return (NOTIFICATION_CHANNELS as readonly string[]).includes(value);
}

export function isPolicyRole(value: string): value is PolicyRole {
  return (POLICY_ROLES as readonly string[]).includes(value);
}

/** Seeded defaults = today's behaviour (per channel, per event). */
export const DEFAULT_NOTIFICATION_POLICY: Record<NotificationEventType, Record<NotificationChannel, boolean>> = {
  assignment:          { EMAIL: true,  IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  chat_mention:        { EMAIL: false, IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  leave_request:       { EMAIL: true,  IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  leave_decision:      { EMAIL: true,  IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  proposal:            { EMAIL: true,  IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  invoice:             { EMAIL: true,  IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  meeting:             { EMAIL: true,  IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  compliance_deadline: { EMAIL: false, IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  document_expiry:     { EMAIL: false, IN_APP: true,  MOBILE_PUSH: true,  BROWSER_PUSH: true },
  morning_digest:      { EMAIL: true,  IN_APP: false, MOBILE_PUSH: false, BROWSER_PUSH: false },
};
